import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";
import { Turnstile, TURNSTILE_SITE_KEY } from "@/components/site/Turnstile";

const API = "/api";

const PRODUCT_OPTIONS = [
  "Video Brochure",
  "Video Boxes",
  "Premium Book",
  "Custom Box",
];

const empty = { name: "", email: "", phone: "", company: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [product, setProduct] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [token, setToken] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Συμπλήρωσε όνομα και email.");
      return;
    }
    if (!consent) {
      toast.error("Πρέπει να αποδεχτείς τους όρους επικοινωνίας.");
      return;
    }
    if (TURNSTILE_SITE_KEY && !token) {
      toast.error("Ολοκλήρωσε την επαλήθευση ασφαλείας.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        product,
        website,
        turnstileToken: token,
      });
      setDone(true);
      setForm(empty);
      setProduct("");
      setConsent(false);
      setToken("");
      toast.success("Το αίτημά σου στάλθηκε! Θα επικοινωνήσουμε σύντομα.");
    } catch (err) {
      toast.error("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      data-text-bump
      className="relative scroll-mt-24 overflow-hidden bg-ink py-24 text-white sm:py-32"
    >
      <div className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-brand-500/30 blur-[110px]" />
      <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-brand-700/30 blur-[110px]" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-300">
            Ζήτησε προσφορά
          </span>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1.04] sm:text-6xl">
            Ας κάνουμε το brand σου{" "}
            <span className="text-brand-400">αξέχαστο.</span>
          </h2>
          <p className="mt-6 max-w-md text-base text-white/70 sm:text-lg">
            Πες μας λίγα λόγια για την ιδέα σου και θα σου προτείνουμε τη λύση
            VideoActive που ταιριάζει στις ανάγκες σου.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          {done ? (
            <div
              data-testid="contact-success"
              className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-3xl bg-white p-10 text-center text-ink"
            >
              <CheckCircle2 className="h-16 w-16 text-brand-500" />
              <h3 className="mt-6 font-display text-2xl font-bold">
                Ευχαριστούμε!
              </h3>
              <p className="mt-3 max-w-xs text-muted-foreground">
                Λάβαμε το αίτημά σου. Η ομάδα της VideoActive θα επικοινωνήσει
                μαζί σου πολύ σύντομα.
              </p>
              <button
                data-testid="contact-reset"
                onClick={() => setDone(false)}
                className="mt-8 rounded-full border border-border px-6 py-3 text-sm font-semibold text-ink hover:bg-secondary"
              >
                Νέο αίτημα
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-3xl bg-white p-7 text-ink shadow-2xl sm:p-9"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink/80">
                    Όνομα *
                  </label>
                  <Input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Το όνομά σου"
                    className="h-11 bg-secondary/60"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink/80">
                    Email *
                  </label>
                  <Input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="email@εταιρεία.gr"
                    className="h-11 bg-secondary/60"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink/80">
                    Τηλέφωνο
                  </label>
                  <Input
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="69xx xxx xxx"
                    className="h-11 bg-secondary/60"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink/80">
                    Εταιρεία
                  </label>
                  <Input
                    data-testid="contact-company"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Η εταιρεία σου"
                    className="h-11 bg-secondary/60"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium text-ink/80">
                  Προϊόν ενδιαφέροντος (προαιρετικό)
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRODUCT_OPTIONS.map((opt) => (
                    <button
                      type="button"
                      key={opt}
                      data-testid={`product-chip-${opt}`}
                      onClick={() => setProduct(product === opt ? "" : opt)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                        product === opt
                          ? "border-brand-500 bg-brand-500 text-white"
                          : "border-border bg-white text-ink/70 hover:border-brand-300"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <label className="mb-1.5 block text-sm font-medium text-ink/80">
                  Μήνυμα (προαιρετικό)
                </label>
                <Textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Πες μας για το project σου…"
                  className="min-h-[110px] bg-secondary/60"
                />
              </div>

              <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-ink/70">
                <Checkbox
                  data-testid="contact-consent"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  className="mt-0.5 data-[state=checked]:border-brand-500 data-[state=checked]:bg-brand-500"
                />
                <span>
                  Αποδέχομαι τους{" "}
                  <Link
                    to="/oroi-epikoinonias"
                    className="font-semibold text-brand-600 underline hover:text-brand-500"
                  >
                    όρους επικοινωνίας
                  </Link>
                  .
                </span>
              </label>

              {/* Honeypot — hidden from users, bots tend to fill it. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />

              {TURNSTILE_SITE_KEY && <Turnstile onVerify={setToken} className="mt-5" />}

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-7 py-4 text-base font-semibold text-white transition-all hover:bg-brand-600 disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Στείλε το αίτημα
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
